import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useOutlet } from '../../context/OutletContext';
import './Header.css';

const Header = ({ cartItemCount = 0, onCartClick, onAuthClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isOutletMenuOpen, setIsOutletMenuOpen] = useState(false);
  const { currentUser, customerProfile, logout } = useCustomerAuth();
  const { outlets, selectedOutlet, selectOutlet } = useOutlet();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const outletMenuRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  const handleSelectOutlet = (outletId) => {
    selectOutlet(outletId);
    setIsOutletMenuOpen(false);
  };

  const handleViewAllOutlets = () => {
    setIsOutletMenuOpen(false);
    navigate('/select-outlet');
  };

  const isActive = (path) => location.pathname === path;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (outletMenuRef.current && !outletMenuRef.current.contains(event.target)) {
        setIsOutletMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && isMenuOpen) {
        const mobileToggle = event.target.closest('.oz-mobile-toggle');
        if (!mobileToggle) {
          setIsMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Close menus on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsOutletMenuOpen(false);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <header className="oz-header">
      <div className="oz-header-container">
        {/* Logo */}
        <Link to="/" className="oz-logo">
          <span className="oz-logo-icon">🍛</span>
          <div className="oz-logo-text">
            <span className="oz-logo-name">Odisha Zaika</span>
            <span className="oz-logo-tagline">Authentic Odia Flavors</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="oz-nav-desktop">
          <Link to="/" className={`oz-nav-link ${isActive('/') ? 'active' : ''}`}>Home</Link>
          <Link to="/menu" className={`oz-nav-link ${isActive('/menu') ? 'active' : ''}`}>Menu</Link>
          <Link to="/about" className={`oz-nav-link ${isActive('/about') ? 'active' : ''}`}>About</Link>
          <Link to="/contact" className={`oz-nav-link ${isActive('/contact') ? 'active' : ''}`}>Contact</Link>
          <Link to="/track-order" className={`oz-nav-link ${isActive('/track-order') ? 'active' : ''}`}>Track Order</Link>
        </nav>

        {/* Right Actions */}
        <div className="oz-header-actions">
          {/* Outlet Selector */}
          {selectedOutlet && outlets.length > 1 && (
            <div className="oz-outlet-menu" ref={outletMenuRef}>
              <button
                className="oz-outlet-btn"
                onClick={() => setIsOutletMenuOpen(!isOutletMenuOpen)}
                aria-label="Select outlet"
                aria-expanded={isOutletMenuOpen}
                aria-haspopup="true"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span className="oz-outlet-name">{selectedOutlet.name}</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {isOutletMenuOpen && (
                <div className="oz-outlet-dropdown" role="menu" aria-label="Outlet selection">
                  <div className="oz-outlet-dropdown-header">
                    <span>Choose Outlet</span>
                  </div>
                  {outlets.filter(o => o.isActive).map(outlet => (
                    <button
                      key={outlet.id}
                      className={`oz-outlet-dropdown-item ${selectedOutlet?.id === outlet.id ? 'active' : ''}`}
                      onClick={() => handleSelectOutlet(outlet.id)}
                      role="menuitem"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <div className="oz-outlet-info">
                        <span className="oz-outlet-dropdown-name">{outlet.name}</span>
                        {outlet.address && <span className="oz-outlet-dropdown-address">{outlet.address}</span>}
                      </div>
                      {selectedOutlet?.id === outlet.id && (
                        <svg className="oz-outlet-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  ))}
                  <div className="oz-outlet-dropdown-divider" role="separator"></div>
                  <button
                    className="oz-outlet-dropdown-item oz-outlet-view-all"
                    onClick={handleViewAllOutlets}
                    role="menuitem"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="16"/>
                      <line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                    View All Outlets
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Cart Button */}
          <button
            className="oz-cart-btn"
            onClick={onCartClick}
            aria-label={`Shopping cart with ${cartItemCount} item${cartItemCount !== 1 ? 's' : ''}`}
            aria-live="polite"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="m1 1 4 4 2 12h13l3-9H6"/>
            </svg>
            {cartItemCount > 0 && (
              <span className="oz-cart-badge" aria-hidden="true">{cartItemCount}</span>
            )}
          </button>

          {/* User Menu */}
          {currentUser ? (
            <div className="oz-user-menu" ref={userMenuRef}>
              <button
                className="oz-user-btn"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-label="User menu"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <div className="oz-user-avatar" aria-hidden="true">
                  {customerProfile?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="oz-user-name">{customerProfile?.name?.split(' ')[0] || 'User'}</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {isUserMenuOpen && (
                <div className="oz-dropdown-menu" role="menu" aria-label="User account menu">
                  <Link to="/profile" className="oz-dropdown-item" onClick={() => setIsUserMenuOpen(false)} role="menuitem">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    My Profile
                  </Link>
                  <Link to="/orders" className="oz-dropdown-item" onClick={() => setIsUserMenuOpen(false)} role="menuitem">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M9 9h6M9 13h6M9 17h4"/>
                    </svg>
                    My Orders
                  </Link>
                  <Link to="/favorites" className="oz-dropdown-item" onClick={() => setIsUserMenuOpen(false)} role="menuitem">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    Favorites
                  </Link>
                  <Link to="/addresses" className="oz-dropdown-item" onClick={() => setIsUserMenuOpen(false)} role="menuitem">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    Addresses
                  </Link>
                  <div className="oz-dropdown-divider" role="separator"></div>
                  <button className="oz-dropdown-item oz-logout-btn" onClick={handleLogout} role="menuitem">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="oz-login-btn" onClick={onAuthClick} aria-label="Login or sign up">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span>Login</span>
            </button>
          )}

          {/* Mobile Menu Toggle */}
          <button
            className="oz-mobile-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              {isMenuOpen ? (
                <path d="M18 6L6 18M6 6l12 12"/>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <nav
        id="mobile-navigation"
        className={`oz-nav-mobile ${isMenuOpen ? 'open' : ''}`}
        ref={mobileMenuRef}
        aria-label="Mobile navigation"
        aria-hidden={!isMenuOpen}
      >
        <Link
          to="/"
          className={`oz-nav-link ${isActive('/') ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(false)}
          tabIndex={isMenuOpen ? 0 : -1}
        >
          Home
        </Link>
        <Link
          to="/menu"
          className={`oz-nav-link ${isActive('/menu') ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(false)}
          tabIndex={isMenuOpen ? 0 : -1}
        >
          Menu
        </Link>
        <Link
          to="/about"
          className={`oz-nav-link ${isActive('/about') ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(false)}
          tabIndex={isMenuOpen ? 0 : -1}
        >
          About
        </Link>
        <Link
          to="/contact"
          className={`oz-nav-link ${isActive('/contact') ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(false)}
          tabIndex={isMenuOpen ? 0 : -1}
        >
          Contact
        </Link>
        <Link
          to="/track-order"
          className={`oz-nav-link ${isActive('/track-order') ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(false)}
          tabIndex={isMenuOpen ? 0 : -1}
        >
          Track Order
        </Link>
        {!currentUser && (
          <button
            className="oz-nav-login-btn"
            onClick={() => { onAuthClick(); setIsMenuOpen(false); }}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Login / Sign Up
          </button>
        )}
      </nav>
    </header>
  );
};

export default Header;
